import {
  Badge,
  ButtonSelect,
  Icon,
  InlineMessage,
  Link,
  Loader,
  Menu,
  MenuMulti,
  type MenuMultiOption,
  type MenuOption,
  Typography,
} from "@saviynt/design-system";
import { defineMessage, useIntl, type MessageDescriptor } from "react-intl";
import {
  isMultipleFilterType,
  useListFieldFilters,
} from "./ListFieldFiltersContext";
import type { ListFilterFieldDefinition } from "./types";

export type FilterFieldPillsProps = {
  definitions: ListFilterFieldDefinition[] | undefined;
  isLoading: boolean;
  isError: boolean;
};

const messages: Record<string, MessageDescriptor> = {
  loading: defineMessage({
    id: "ZZ3fM2",
    defaultMessage: "Loading filters…",
    description: "Loading state for filter options",
  }),
  error: defineMessage({
    id: "PULGHE",
    defaultMessage: "Could not load filters. Try again.",
    description: "Error when filter API fails",
  }),
  clear: defineMessage({
    id: "fJhk1B",
    defaultMessage: "Clear",
    description: "Clear current filter field selection",
  }),
};

function getSingleTriggerLabel(
  fieldLabel: string,
  selectedValue: string | undefined,
  options: { value: string; label: string }[],
): string {
  if (!selectedValue) {
    return fieldLabel;
  }
  const opt = options.find((o) => o.value === selectedValue);
  return opt ? `${fieldLabel}: ${opt.label}` : fieldLabel;
}

function getMultiTriggerLabel(
  fieldLabel: string,
  selectedValues: string[],
  options: { value: string; label: string }[],
): string {
  if (selectedValues.length === 0) {
    return fieldLabel;
  }
  if (selectedValues.length === 1) {
    const opt = options.find((o) => o.value === selectedValues[0]);
    return opt ? `${fieldLabel}: ${opt.label}` : fieldLabel;
  }
  return `${fieldLabel} (${selectedValues.length})`;
}

function FilterFieldSingleMenu({
  definition,
}: {
  definition: ListFilterFieldDefinition;
}) {
  const intl = useIntl();
  const { field, label, type, options: defOptions } = definition;
  const { toggleFilterOption, clearField, getSelection, isFieldActive } =
    useListFieldFilters();
  const selected = getSelection(field, type) as string | undefined;
  const hasSelection = isFieldActive(field, type);

  const menuOptions: MenuOption[] = defOptions.map((opt) => ({
    value: opt.value,
    label: opt.label,
    isChecked: selected === opt.value,
  }));

  return (
    <Menu
      kind="simple"
      options={menuOptions}
      onChange={(opt) => {
        toggleFilterOption(field, type, opt.value as string);
      }}
      trigger={
        <ButtonSelect
          kind="pill"
          size="medium"
          label={getSingleTriggerLabel(label, selected, defOptions)}
          prefixIcon={<Icon kind="Filter" size="small" />}
          BadgeComp={
            hasSelection ? (
              <Badge kind="dot" color="primary" size="small" />
            ) : undefined
          }
          dataTestId={`list-field-filter-menu-${field}`}
        />
      }
      footerComp={
        <Link
          text={intl.formatMessage(messages.clear)}
          kind="button"
          onClick={() => clearField(field)}
          size="small"
        />
      }
    />
  );
}

function FilterFieldMultiMenu({
  definition,
}: {
  definition: ListFilterFieldDefinition;
}) {
  const intl = useIntl();
  const { field, label, type, options: defOptions } = definition;
  const { setFilterValue, clearField, getSelection, isFieldActive } =
    useListFieldFilters();
  const selected = (getSelection(field, type) as string[] | undefined) ?? [];
  const hasSelection = isFieldActive(field, type);

  const menuOptions: MenuMultiOption[] = defOptions.map((opt) => ({
    value: opt.value,
    label: opt.label,
    isChecked: selected.includes(opt.value),
  }));

  return (
    <MenuMulti
      label={getMultiTriggerLabel(label, selected, defOptions)}
      options={menuOptions}
      onChange={(next) => {
        if (!next) {
          setFilterValue(field, type, null);
          return;
        }
        const values = next
          .filter((o) => o.isChecked)
          .map((o) => o.value as string);
        setFilterValue(field, type, values.length ? values : null);
      }}
      trigger={
        <ButtonSelect
          kind="pill"
          size="medium"
          label=""
          prefixIcon={<Icon kind="Filter" size="small" />}
          BadgeComp={
            hasSelection ? (
              <Badge kind="dot" color="primary" size="small" />
            ) : undefined
          }
          dataTestId={`list-field-filter-multimenu-${field}`}
        />
      }
      minOptionsForSearch={0}
      heightToShowScrollbar="12rem"
      appendLabelsToButton={false}
      footerLink2Text={intl.formatMessage(messages.clear)}
      onFooterLink2Click={() => clearField(field)}
    />
  );
}

function FilterFieldMenuRow({
  definition,
}: {
  definition: ListFilterFieldDefinition;
}) {
  if (isMultipleFilterType(definition.type)) {
    return <FilterFieldMultiMenu definition={definition} />;
  }
  return <FilterFieldSingleMenu definition={definition} />;
}

/** Menu / MenuMulti pill controls for static filter definitions. Use with `ListFieldFiltersProvider`. */
export function FilterFieldPills({
  definitions,
  isLoading,
  isError,
}: FilterFieldPillsProps) {
  const intl = useIntl();

  if (isLoading) {
    return (
      <div
        className="flex flex-row items-center gap-2"
        data-testid="list-field-filter-pills-loading"
      >
        <Loader kind="loop" format="inline" color="brand" size="small" />
        <Typography kind="body2" color="--color-foreground-primary-medium">
          {intl.formatMessage(messages.loading)}
        </Typography>
      </div>
    );
  }

  if (isError) {
    return (
      <div
        className="flex flex-row items-center gap-2"
        data-testid="list-field-filter-pills-error"
      >
        <InlineMessage
          text={intl.formatMessage(messages.error)}
          colorTheme="critical"
          kind="background"
          size="small"
          leftIcon={<Icon kind="AlertCriticalFilled" />}
        />
      </div>
    );
  }

  if (!definitions?.length) {
    return null;
  }

  return (
    <div
      className="flex flex-row flex-wrap items-center gap-2"
      data-testid="list-field-filter-pills"
    >
      {definitions.map((def) => (
        <FilterFieldMenuRow key={def.field} definition={def} />
      ))}
    </div>
  );
}
